import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import classNames from 'classnames';
import SignaturePad from 'signature_pad';
import { useFocusFieldEvent } from '../../../contexts/events-context';
import { useTranslation } from '../../../contexts/translation-context';
import { dataUrlToWixFile } from '../utils/data-url';
import { resizeCanvas } from '../utils/canvas';
import { decodePoints, encodePoints } from '../utils/points';
import styles from './signature-drawer.module.scss';
export const SignatureDrawer = props => {
  const {
    className,
    onChange,
    value,
    theme,
    penColor,
    inputId,
    onBeforeChange,
    fieldId,
    onBlur,
    onFocus,
    defaultValue,
    hidden,
    readOnly
  } = props;
  const pad = useRef();
  const canvasRef = useRef();
  const resizeObserver = useRef(null);
  const valueUrl = value == null ? void 0 : value.url;
  const {
    t
  } = useTranslation();
  const [ariaLabel, setAriaLabel] = useState(t('field.signature.canvas.aria-label.empty'));
  useFocusFieldEvent(() => {
    var _canvasRef$current;
    (_canvasRef$current = canvasRef.current) == null || _canvasRef$current.focus();
  }, fieldId);
  const options = useMemo(() => ({
    throttle: 0,
    dotSize: 2,
    minWidth: 1,
    maxWidth: 3,
    velocityFilterWeight: 0.2,
    penColor
  }), [penColor]);
  const getSignature = useCallback(() => {
    var _pad$current, _pad$current2, _pad$current3;
    if ((_pad$current = pad.current) != null && _pad$current.isEmpty()) {
      return '';
    }
    const dataPoints = (_pad$current2 = pad.current) == null ? void 0 : _pad$current2.toData();
    return dataPoints ? encodePoints(dataPoints) : ((_pad$current3 = pad.current) == null ? void 0 : _pad$current3.toDataURL()) ?? '';
  }, []);
  const handleChange = useCallback(() => {
    var _pad$current4;
    const isEmpty = (_pad$current4 = pad.current) == null ? void 0 : _pad$current4.isEmpty();
    setAriaLabel(isEmpty ? t('field.signature.canvas.aria-label.empty') : t('field.signature.canvas.aria-label.signed'));
    onChange == null || onChange(dataUrlToWixFile(getSignature()));
  }, [getSignature, onChange, t]);
  const handleBeforeChange = useCallback(() => {
    onBeforeChange == null || onBeforeChange();
  }, [onBeforeChange]);
  const mountCanvas = useCallback(node => {
    const isMount = node != null;
    if (isMount) {
      var _resizeObserver$curre;
      canvasRef.current = node;
      pad.current = new SignaturePad(node, options);
      resizeCanvas(canvasRef.current, pad.current);
      if (valueUrl) {
        const points = decodePoints(valueUrl);
        if (points) {
          pad.current.fromData(points);
          setAriaLabel(t('field.signature.canvas.aria-label.signed'));
        }
      } else {
        pad.current.clear();
        setAriaLabel(t('field.signature.canvas.aria-label.empty'));
      }
      pad.current.addEventListener('beginStroke', handleBeforeChange);
      pad.current.addEventListener('endStroke', handleChange);
      if (typeof ResizeObserver !== 'undefined' && !resizeObserver.current) {
        // SSR compatibility for ResizeObserver
        resizeObserver.current = new ResizeObserver(() => {
          if (canvasRef.current) {
            resizeCanvas(canvasRef.current, pad == null ? void 0 : pad.current);
          }
        });
      }
      (_resizeObserver$curre = resizeObserver.current) == null || _resizeObserver$curre.observe(node);
    } else {
      var _resizeObserver$curre2, _pad$current5;
      // Cleanup on unmount
      (_resizeObserver$curre2 = resizeObserver.current) == null || _resizeObserver$curre2.disconnect();
      (_pad$current5 = pad.current) == null || _pad$current5.removeEventListener('endStroke', handleChange);
      window.removeEventListener('beginStroke', handleBeforeChange);
    }
  },
  // onChange prop that comes from form root changes every render - it must be excluded from deps list
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [options, t, valueUrl, handleBeforeChange]);
  useEffect(() => {
    if (!pad.current) {
      return;
    }
    if (readOnly) {
      pad.current.off();
    } else {
      pad.current.on();
    }
  }, [readOnly]);
  useFocusFieldEvent(() => {
    var _canvasRef$current2;
    (_canvasRef$current2 = canvasRef.current) == null || _canvasRef$current2.focus();
  }, fieldId);
  return /*#__PURE__*/React.createElement("div", {
    className: className,
    "data-theme": theme
  }, /*#__PURE__*/React.createElement("canvas", {
    hidden: hidden,
    defaultValue: defaultValue,
    onFocus: onFocus,
    onBlur: onBlur,
    ref: mountCanvas,
    id: inputId,
    "data-input-id": inputId,
    "data-has-value": !!valueUrl,
    className: classNames(styles.canvas),
    "data-hook": "signature-canvas",
    role: "img",
    "aria-label": ariaLabel
  }));
};
//# sourceMappingURL=signature-drawer.js.map